import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  Alert,
  FormLabel,
} from "react-bootstrap";
import SimpleHeader from "components/Headers/SimpleHeader";
import { serverHost } from "variables/serverdata";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { Input } from "reactstrap";

const dialPatternOptions = [
  { label: "", value: "" },
  { label: "2 Digits", value: "^\\d{2}$" },
  { label: "3 Digits", value: "^\\d{3}$" },
  { label: "4 Digits", value: "^\\d{4}$" },
  { label: "5 Digits", value: "^\\d{5}$" },
  { label: "6 Digits", value: "^\\d{6}$" },
  { label: "7 Digits Local", value: "^\\d{7}$" },
  { label: "8 Digits", value: "^\\d{8}$" },
  { label: "9 Digits", value: "^\\d{9}$" },
  { label: "10 Digits Long Distance", value: "^\\d{10}$" },
  { label: "11 Digits Long Distance", value: "^\\+?\\d{11}$" },
  {
    label: "North America",
    value: "^(?:\\+1|1)?([2-9]\\d{2}[2-9]\\d{2}\\d{4})$",
  },
  { label: "North America International", value: "^(011\\d{9,17})$" },
  {
    label: "North America Islands",
    value:
      "^(?:\\+1|1)?((?:264|268|242|246|441|284|345|767|809|829|849|473|658|876|664|787|939|869|758|784|721|868|649|340|684|671|670|808)\\d{7})$",
  },
  { label: "Europe International", value: "^(00\\d{9,17})$" },
  { label: "International", value: "^\\d{12,20}$" },
  { label: "311 Information", value: "^311$" },
  { label: "411 Information", value: "^411$" },
  { label: "711 TTY", value: "^711$" },
  { label: "911 Emergency", value: "(^911$|^933$)" },
  { label: "988 National Suicide Prevention Lifeline", value: "^988$" },
  {
    label: "Toll-Free",
    value: "^(?:\\+1|1)?(8(00|33|44|55|66|77|88)[2-9]\\d{6})$",
  },
  { label: "iNum 0118335100xxxxxxxx", value: "^0118835100\\d{8}$" },
  { label: "Dial 9, then 2 Digits", value: "^9\\d{2}$" },
  { label: "Dial 9, then 3 Digits", value: "^9\\d{3}$" },
  { label: "Dial 9, then 4 Digits", value: "^9\\d{4}$" },
  { label: "Dial 9, then 5 Digits", value: "^9\\d{5}$" },
  { label: "Dial 9, then 6 Digits", value: "^9\\d{6}$" },
  { label: "Dial 9, then 7 Digits", value: "^9\\d{7}$" },
  { label: "Dial 9, then 8 Digits", value: "^9\\d{8}$" },
  { label: "Dial 9, then 9 Digits", value: "^9\\d{9}$" },
  { label: "Dial 9, then 10 Digits", value: "^9\\d{10}$" },
  { label: "Dial 9, then 11 Digits", value: "^9\\d{11}$" },
  { label: "Dial 9, then International", value: "^9\\d{12,20}$" },
  { label: "Dial custom prefix, then Digits", value: "CUSTOM_PREFIX" },
];

const DialplanOutboundAdd = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const currentSite = JSON.parse(localStorage.getItem("selectedSite"));
  const domain_uuid = currentSite?.site_uuid || null;
  const [formData, setFormData] = useState({
    gateway: "",
    gateway_type: "",
    gateway_uuid: "",
    alt_1: "",
    alt_1_type: "",
    alt_1_uuid: "",
    alt_2: "",
    alt_2_type: "",
    alt_2_uuid: "",
    dialplan_expression: "",
    dialplan_option: "",
    prefix: "",
    limit: "",
    account_code: "",
    toll_allow: "false",
    pin: "false",
    order: 100,
    enabled: "true",
    description: null,
  });

  const [gatewayList, setGatewayList] = useState([]);
  const [bridgeList, setBridgeList] = useState([]);
  const [error, setError] = useState("");
  const additional = ["enum", "freetdm", "transfer", "xmpp", "hangup"];
  useEffect(() => {
    const fetchGateways = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_rows?table=v_gateways&pageSize=50&page=1&domain_uuid=${encodeURIComponent(domain_uuid)}`,
        );
        if (res.ok) {
          const data = await res.json();
          const gateways = data.results.map((g) => ({
            gateway_uuid: g.gateway_uuid,
            name: g.gateway,
          }));
          console.log("Gateways: ", gateways);
          setGatewayList(gateways);
        }
      } catch (err) {
        console.error("Failed to fetch gateways:", err);
      }
    };
    const fetchBridges = async () => {
      try {
        const res = await fetch(
          `${serverHost}/get_rows?table=v_bridges&pageSize=50&page=1&domain_uuid=${encodeURIComponent(domain_uuid)}`,
        );
        if (res.ok) {
          const data = await res.json();
          const bridges = data.results.map((b) => ({
            destination: b.bridge_destination,
            bridge_uuid: b.bridge_uuid,
            name: b.bridge_name,
          }));
          console.log(bridges);
          setBridgeList(bridges);
        }
      } catch (err) {
        console.error("Failed to fetch bridges:", err);
      }
    };

    fetchGateways();
    fetchBridges();
  }, []);

  const handleBack = () => {
    if (
      Object.values(formData).some((val) => val !== "" && val !== null) &&
      !window.confirm("Discard changes and go back?")
    )
      return;
    navigate("/admin/hucs/outbound-routes");
  };

  const handleChange = (eOrName, value) => {
    // If called with an event
    if (eOrName?.target) {
      const { name, type, checked, value } = eOrName.target;
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
    // If called manually: handleChange("field", value)
    else {
      setFormData((prev) => ({
        ...prev,
        [eOrName]: value,
      }));
    }
  };

  const handleDialOptionChange = (e) => {
    const value = e.target.value;

    setFormData((prev) => ({
      ...prev,
      dialplan_option: value,
      // Blank option clears the expression; custom prefix leaves it for the user to fill in
      dialplan_expression:
        value === "" ? "" : value === "CUSTOM_PREFIX" ? "" : value,
    }));
  };

  const handleGateChange = (e) => {
    const field = e.target.name;
    const rawValue = e.target.value;

    // Handle empty selection safely
    if (!rawValue) {
      setFormData((prev) => ({
        ...prev,
        [field]: "",
        [`${field}_type`]: "",
        [`${field}_uuid`]: "",
      }));
      return;
    }

    const [type, value, uuid] = rawValue.split(":");

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}_type`]: type,
      [`${field}_uuid`]: uuid || "",
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    const missingRequired = [];
    if (!formData.gateway) missingRequired.push("gateway");
    if (!formData.dialplan_expression && !formData.dialplan_option)
      missingRequired.push("dialplan expression OR dialplan option");
    if (missingRequired.length) {
      setError(`Missing required fields: ${missingRequired.join(", ")}`);
      return;
    }

    setError("");
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));

      const payload = {
        data: {
          ...formData,
          dialplan_context: "public",
          dialplan_type: "outbound",
        },
        client_uuid: site.client_uuid,
        site_name: site.company_name,
        insert_uuid: JSON.parse(localStorage.getItem("user")).user_uuid,
      };

      const res = await fetch(
        `${serverHost}/insert_outbound/${domain_uuid}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        let fail = "";
        if (res.status === 403) fail = "; Limit reached";
        throw new Error("Failed to insert record" + fail);
      }

      navigate("/admin/hucs/outbound-routes");
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
  };

  const labelStyle = {
    fontWeight: "bolder",
    color: isDark ? "#e2e8f0" : "#1e3a5f",
    marginBottom: "0.5rem",
  };

  return (
    <>
      <SimpleHeader name="Add Outbound Route" parentName="HUCS" />
      <Container className="mt--6">
        <Card className="shadow-sm" style={{ marginTop: "-3rem" }}>
          <Card.Header
            style={{
              backgroundColor: isDark ? "#1f2937" : "#e9ecef",
              color: isDark ? "#e2e8f0" : "#1e3a5f",
              borderBottom: "none",
              padding: "1rem 1.5rem",
            }}
          >
            <h4 className="mb-0">ADD OUTBOUND ROUTE</h4>
          </Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}

            <Form className="edit-form">
              {/* Info Section */}
              <div className="mb-4">
                <Row className="d-flex justify-content-between align-items-center">
                  <h5
                    className="section-title mb-3"
                    style={{ fontSize: "1.5rem", fontWeight: "700" }}
                  >
                    Info
                  </h5>
                </Row>

                <Row>
                  {/* Gateway Dropdown */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label style={labelStyle}>
                        Gateway <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Input
                        type="select"
                        name="gateway"
                        value={`${formData.gateway_type}:${formData.gateway}:${formData.gateway_uuid}`}
                        onChange={handleGateChange}
                      >
                        <option value="">Select Gateway...</option>
                        <optgroup label="Gateways">
                          {gatewayList.map((gw) => (
                            <option
                              key={gw.gateway_uuid}
                              value={`gateway:${gw.name}:${gw.gateway_uuid}`}
                            >
                              {gw.name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Bridges">
                          {bridgeList.map((br) => (
                            <option
                              key={br.bridge_uuid}
                              value={`bridge:${br.name}:${br.bridge_uuid}`}
                            >
                              {br.name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Additional">
                          {additional.map((value) => (
                            <option
                              key={value}
                              value={`additional:${value}:${value}`}
                            >
                              {value}
                            </option>
                          ))}
                        </optgroup>
                      </Input>
                    </Form.Group>
                  </Col>

                  {/* Alt1 Dropdown */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Alt1</Form.Label>
                      {/* Alt1 Dropdown */}
                      <Input
                        type="select"
                        name="alt_1"
                        value={`${formData.alt_1_type}:${formData.alt_1}:${formData.alt_1_uuid}`}
                        onChange={handleGateChange}
                      >
                        <option value="">Select Gateway...</option>
                        <optgroup label="Gateways">
                          {gatewayList.map((gw) => (
                            <option
                              key={gw.gateway_uuid}
                              value={`gateway:${gw.name}:${gw.gateway_uuid}`}
                            >
                              {gw.name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Bridges">
                          {bridgeList.map((br) => (
                            <option
                              key={br.bridge_uuid}
                              value={`bridge:${br.name}:${br.bridge_uuid}`}
                            >
                              {br.name}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Additional">
                          {additional.map((value) => (
                            <option
                              key={value}
                              value={`additional:${value}:${value}`}
                            >
                              {value}
                            </option>
                          ))}
                        </optgroup>
                      </Input>
                    </Form.Group>
                  </Col>
                  {/* Alt2 Dropdown */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Alt2</Form.Label>
                      <Input
                        type="select"
                        name="alt_2"
                        value={`${formData.alt_2_type}:${formData.alt_2}:${formData.alt_2_uuid}`}
                        onChange={handleGateChange}
                      >
                        <option value="">Select Gateway...</option>

                        <optgroup label="Gateways">
                          {gatewayList.map((gw) => (
                            <option
                              key={gw.gateway_uuid}
                              value={`gateway:${gw.name}:${gw.gateway_uuid}`}
                            >
                              {gw.name}
                            </option>
                          ))}
                        </optgroup>

                        <optgroup label="Bridges">
                          {bridgeList.map((br) => (
                            <option
                              key={br.bridge_uuid}
                              value={`bridge:${br.name}:${br.bridge_uuid}`}
                            >
                              {br.name}
                            </option>
                          ))}
                        </optgroup>

                        <optgroup label="Additional">
                          {additional.map((value) => (
                            <option
                              key={value}
                              value={`additional:${value}:${value}`}
                            >
                              {value}
                            </option>
                          ))}
                        </optgroup>
                      </Input>
                    </Form.Group>
                  </Col>

                  {/* Dialplan Expression */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Dialplan Expression</Form.Label>
                      <Form.Control
                        type="text"
                        name="dialplan_expression"
                        value={formData.dialplan_expression}
                        onChange={handleChange}
                        style={{ color: isDark ? "#fafafa" : "#000000" }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Dialplan Options */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <FormLabel>Dialplan Order Option</FormLabel>
                      <Input
                        type="select"
                        name="dialplan_option"
                        value={formData.dialplan_option}
                        onChange={handleDialOptionChange}
                      >
                        {dialPatternOptions.map((dpo) => (
                          <option key={dpo.value} value={dpo.value}>
                            {dpo.label}
                          </option>
                        ))}
                      </Input>
                    </Form.Group>
                  </Col>

                  {/* Prefix */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Prefix</Form.Label>
                      <Form.Control
                        type="text"
                        name="prefix"
                        value={formData.prefix}
                        onChange={handleChange}
                        style={{ color: isDark ? "#fafafa" : "#000000" }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Limit */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Limit</Form.Label>
                      <Form.Control
                        type="text"
                        name="limit"
                        value={formData.limit}
                        onChange={handleChange}
                        style={{ color: isDark ? "#fafafa" : "#000000" }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Account Code */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Account Code</Form.Label>
                      <Form.Control
                        type="text"
                        name="account_code"
                        value={formData.account_code}
                        onChange={handleChange}
                        style={{ color: isDark ? "#fafafa" : "#000000" }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Toll Allow (Boolean) */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                          Toll Allow
                        </span>
                        <label className="custom-toggle mb-0">
                          <input
                            type="checkbox"
                            id="toll_allow"
                            name="toll_allow"
                            checked={formData.toll_allow}
                            onChange={handleChange}
                          />
                          <span
                            className="custom-toggle-slider rounded-circle"
                            data-label-off="Off"
                            data-label-on="On"
                          />
                        </label>
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Pin */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                          Pin
                        </span>
                        <label className="custom-toggle mb-0">
                          <input
                            type="checkbox"
                            id="pin"
                            name="pin"
                            checked={formData.pin}
                            onChange={handleChange}
                          />
                          <span
                            className="custom-toggle-slider rounded-circle"
                            data-label-off="Off"
                            data-label-on="On"
                          />
                        </label>
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Order (Boolean?) */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Order</Form.Label>
                      <Form.Control
                        type="number"
                        min={100}
                        name="order"
                        value={formData.order}
                        onChange={handleChange}
                        style={{ color: isDark ? "white" : undefined }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Enabled (Boolean) */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.5rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <span style={{ color: isDark ? "#e2e8f0" : "#495057" }}>
                          Enabled
                        </span>
                        <label className="custom-toggle mb-0">
                          <input
                            type="checkbox"
                            id="enabled"
                            name="enabled"
                            checked={formData.enabled}
                            onChange={handleChange}
                          />
                          <span
                            className="custom-toggle-slider rounded-circle"
                            data-label-off="Off"
                            data-label-on="On"
                          />
                        </label>
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Description */}
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.description}
                        onChange={(e) =>
                          handleChange("description", e.target.value)
                        }
                        style={{ color: isDark ? "#ffffff" : "#080808" }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 d-flex justify-content-between">
                <Button variant="danger" onClick={handleBack}>
                  Cancel
                </Button>
                <Button
                  variant="success"
                  onClick={handleSave}
                  disabled={
                    !formData.gateway ||
                    !(
                      formData.dialplan_expression || formData.dialplan_option
                    ) ||
                    !formData.order ||
                    !formData.enabled
                  }
                >
                  Save
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default DialplanOutboundAdd;
